import * as types from '../../actiontypes';

import {GetAllTasksUrl, GetAssignmentsByTasksIdUrl, GetTaskByIdUrl, createApiAction} from '../../apiUrls';
import {
	TaskCategory,
	TaskSubCategory,
	TaskTypeEnum,
	canvasUrlParams,
	promiseAreas,
	taskBuildMilestoneIds,
	taskSortingOrder,
	TaskApiSubCategoryId,
	TaskApiFilterRelated
} from '../../../util/uiconstants';

/**
 * Returns an action to be dispatched and treated by the api-middleware, ultimately making an API call through Axios.
 * Represents a request for all a particular Task Group in the specified Engagement, from the specified Task Category and Sub-Category IDs.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} taskBuildMilestoneId The Task Milestone ID.
 * @param {String} orderBy The order by key to indicate the sorting order.
 * @param {Number} [categoryId = 2] The Task Category ID.
 * @param {Number} [subCategoryId = 1] The Task Sub-Category ID.
 * @param {boolean} includeCounts include counts
 * @param {boolean} includeBookmarks include bookmarks
 * @param {boolean} includePagination include pagination
 * @param {boolean} includeUIElements include UI Elements
 * @param {boolean} includeScheduledInfo include Scheduled Info
 * @param {boolean} includeAssignments include Assignments
 * @param {boolean} includeTaskList include task list
 * @returns {Object} An action object to be dispatched and treated by the api-middleware.
 */
export const getAllTasks = (
	engagementId,
	taskBuildMilestoneId = taskBuildMilestoneIds.GuidedWorkflow,
	orderBy,
	categoryId = TaskCategory.all,
	subCategoryId = TaskSubCategory.all,
	includeCounts = false,
	includeBookmarks = false,
	includePagination = false,
	includeUIElements = false,
	includeScheduledInfo = false,
	includeAssignments = false,
	includeTaskList = false,
	filterTaskIds = [],
	isAresV2 = false
) => {
	const parameters = {
		[canvasUrlParams.taskType]: TaskTypeEnum.PRIMARY_STANDARD_TASK,
		[canvasUrlParams.categoryId]: categoryId,
		[canvasUrlParams.subCategoryId]: subCategoryId,
		[canvasUrlParams.includeCounts]: includeCounts,
		[canvasUrlParams.includePagination]: includePagination,
		[canvasUrlParams.includeBookmarks]: includeBookmarks,
		[canvasUrlParams.includeUIElements]: includeUIElements,
		[canvasUrlParams.includeScheduledInfo]: includeScheduledInfo,
		[canvasUrlParams.includeAssignments]: includeAssignments,
		[canvasUrlParams.includeTaskList]: includeTaskList,
		[canvasUrlParams.taskBuildMilestone]: taskBuildMilestoneId,
		pageSize: 100
	};

	if (orderBy && orderBy !== '') {
		parameters['orderBy'] = orderBy;
	}

	const url = GetAllTasksUrl(engagementId, parameters, filterTaskIds);

	return createApiAction(types.GET_ALL_TASKS_BY_GROUP_ID, url, 'GET', undefined, undefined, {resolve: true, isAresV2});
};

export const getAllAresTasks = (engagementId, orderBy = taskSortingOrder.knowledge, filterTaskIds, isAresV2) => {
	return getAllTasks(
		engagementId,
		taskBuildMilestoneIds.GuidedWorkflow,
		orderBy,
		TaskCategory.all,
		TaskSubCategory.all,
		false,
		false,
		false,
		false,
		false,
		true,
		true,
		filterTaskIds,
		isAresV2
	);
};

/**
 * Returns an action to be dispatched and treated by the api-middleware, ultimately making an API call through Axios.
 * Represents a request for getting a task by its id.
 * @param {Number} engagementId The Engagement ID.
 * @param {Number} taskId The Task ID.
 * @param {string} queryString queryString if any
 * @returns {Object} An action object to be dispatched and treated by the api-middleware.
 */
export function getTaskById(engagementId, taskId, queryString) {
	const url = GetTaskByIdUrl(engagementId, taskId, queryString);

	const apiAction = createApiAction(
		types.ARES_GET_TASK_BY_ID,
		url,
		'GET',
		undefined,
		undefined,
		{resolve: true},
		undefined,
		promiseAreas.FIT_BREADCRUMB
	);
	apiAction.taskId = taskId;

	return apiAction;
}

export function updateTaskAssignmentStatus(engagementId, taskId, payload) {
	const url = GetTaskByIdUrl(engagementId, taskId);
	return {
		type: types.CHANGE_TASK_ASSIGNMENT_STATUS,
		meta: {
			api: {
				url: url,
				method: 'PATCH',
				data: payload
			}
		},
		payload
	};
}

export function updateTaskStatus(engagementId, taskId, payload) {
	const url = GetTaskByIdUrl(engagementId, taskId);
	return {
		type: types.CHANGE_TASK_STATUS,
		meta: {
			api: {
				url: url,
				method: 'PATCH',
				data: payload
			}
		},
		payload
	};
}

export function acceptOrRejectTask(engagementId, taskId, payload, resolve = false) {
	const url = GetTaskByIdUrl(engagementId, taskId);
	const actionType = payload[0].path === 'rejecttask' || payload[0].path === 'editRationale' ? types.REJECT_TASK : types.ACCEPT_TASK;
	return {
		type: actionType,
		meta: {
			api: {
				url: url,
				method: 'PATCH',
				data: [payload[0]]
			}
		},
		payload,
		resolve: resolve
	};
}

export function removeTask(url, taskData = {}) {
	return {
		type: types.REMOVE_TASK,
		payload: taskData,
		meta: {
			api: {
				url: url,
				method: 'DELETE'
			}
		}
	};
}

export function removeTaskAccount(url, taskData = {}) {
	return {
		type: types.REMOVE_TASK_ACCOUNT,
		payload: taskData,
		meta: {
			api: {
				url: url,
				method: 'DELETE'
			}
		}
	};
}

export function updateTaskAssertion(url, data = {}, taskData = {}) {
	return {
		type: types.UPDATE_TASK_ASSERTION,
		payload: taskData,
		meta: {
			api: {
				url: url,
				method: 'POST',
				data: data
			}
		}
	};
}
export function deleteTaskAssertions(url, taskData = {}) {
	return {
		type: types.DELETE_TASK_ASSERTION,
		payload: taskData,
		meta: {
			api: {
				url: url,
				method: 'DELETE'
			}
		}
	};
}
export function getAssignmentsByTaskId(engagementId, taskId) {
	const url = GetAssignmentsByTasksIdUrl(engagementId, taskId);
	const apiAction = createApiAction(types.GET_TASK_ASSIGNMENTS, url, 'GET');
	apiAction.taskId = taskId;
	return apiAction;
}

/**
 * Retrieves unrelated tasks for a specific engagement based on filtering criteria.
 *
 * @function getUnrelateTasks
 * @param {string|number} engagementId - The ID of the engagement to retrieve tasks for
 * @param {number} entity - The entity to place the data in specific object. Ex: ITProcess, Scots, Controls etc.,
 * @param {number} categoryid - The category ID to filter tasks by
 * @param {number|number[]} taskTypeId - The task type ID(s) to filter tasks by. Can be a single number or an array of numbers
 * @param {string} searchText - The text to search for within tasks
 * @param {number} page - The current page number for pagination
 * @param {number} pageSize - The number of items per page for pagination
 * @param {string} [validationGroup] - Optional validation group. If provided, uses a simplified parameter set filtered by validationGroup instead of the standard task filters
 * @returns {Object} Redux action object with type GET_UNRELATED_TASK, containing API metadata and payload
 */
export function getUnrelateTasks(engagementId, entity, categoryid, taskTypeId, searchText='', page, pageSize, validationGroup) {
	const parameters = validationGroup
		? {
			filterValidationGroup: validationGroup,
			orderBy: taskSortingOrder.name,
			orderByAscending: true,
			currentPage: page,
			pageSize: pageSize
		}
		: {
			categoryId: categoryid,
			subcategoryid: TaskApiSubCategoryId.All, // defaulting 1 for all subcategory
			taskType: taskTypeId,
			filterrelatedtask: TaskApiFilterRelated.UnRelated, // 2 indicates unrelate tasks
			orderBy: taskSortingOrder.name,
			orderByAscending: true,
			currentPage: page,
			pageSize: pageSize
		};

	if (searchText && searchText.trim().length > 0) {
		parameters.searchString = searchText.trim();
	}

	const engId = typeof engagementId === 'number' ? engagementId : parseInt(engagementId);
	const url = GetAllTasksUrl(engId, parameters);
	return {
		type: types.GET_UNRELATED_TASK,
		meta: {
			api: {
				url: url,
				method: 'GET'
			}
		},
		payload: {data: entity},
		resolve: true
	};
}
